<template>
    <div
        v-if="activeTab === 'logs'"
        class="tab logs"
        :class="{ enlarged: isEnlarged }"
    >
        <div class="log-lines-container">
            <button
                class="toggle-time-button"
                :title="showTime ? 'Hide timestamps' : 'Show timestamps'"
                @click="toggleShowTime"
            >
                [HH:MM]
            </button>

            <div
                ref="logLines"
                class="log-lines"
            >
                <button
                    v-if="history.archive !== ''"
                    class="archive-button"
                    @click="openArchive"
                >
                    {{ showArchive ? 'Showing full history' : 'View full history' }}
                </button>

                <!-- A game with the rules engine : its own log, the chat among its lines -->
                <EngineLog
                    v-if="gameState.gameType == GameType.Engine"
                    :logEntries="displayedLogEntries"
                    :showTime="showTime"
                />
                <template v-else>
                    <LogLine
                        v-for="(logEntry, index) in displayedLogEntries"
                        :key="'logEntry-' + index"
                        :logEntry="logEntry"
                        :index="index"
                        :showTime="showTime"
                    />
                </template>
            </div>
        </div>

        <div
            v-if="players.isPlayer"
            class="chat-box"
        >
            <input
                ref="chatInput"
                v-model="chatMessageText"
                type="text"
                class="chat-input"
                placeholder="Say something..."
                @keydown.stop
                @keyup.stop
                @keydown.enter="sendChatMessage"
            />
            <button
                class="chat-send"
                @click="sendChatMessage"
            >
                Send
            </button>
        </div>
    </div>

    <div
        v-if="activeTab === 'menu'"
        class="tab menu"
        :class="{ enlarged: isEnlarged }"
    >
        <button
            class="game-button"
            @click="bus.isUserProfilePanelOpen = true"
        >
            User preferences
        </button>

        <button
            class="game-button"
            @click="leaveDialog?.showModal()"
        >
            Leave game
        </button>
    </div>

    <div
        v-if="isEnlarged"
        class="tab placeholder"
    />

    <div
        v-if="activeTab === 'manual'"
        class="tab manual"
        :class="{ enlarged: isEnlarged }"
    >
        <UserManual />
    </div>

    <div class="tabs">
        <button
            class="tab-button enlarge"
            :class="{ active: isEnlarged }"
            @click="toggleEnlarged"
        >
            {{ isEnlarged ? '⇲' : '⇱' }}
        </button>

        <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-button"
            :class="{ active: activeTab === tab.id, enlarged: isEnlarged }"
            @click="activeTab = tab.id"
        >
            {{ tab.title }}
        </button>
    </div>

    <!--Heads-up on the Manual tab for first-time users -->
    <div
        v-if="showManualHeadsUp"
        class="manual-heads-up"
        :class="{ enlarged: isEnlarged }"
    >
        <div class="heads-up-content">
            <div class="heads-up-message">
                📖 Check out the <strong>Manual</strong> tab for help & game commands !
            </div>
            <button
                class="heads-up-button"
                @click="dismissManualHeadsUp"
            >
                Got it
            </button>
        </div>
        <div class="heads-up-arrow" />
    </div>

    <!-- Overlay backdrop when enlarged -->
    <div
        v-if="isEnlarged"
        class="enlarged-backdrop"
        @click="toggleEnlarged"
    />

    <!-- Leave game confirmation dialog -->
    <dialog
        ref="leaveDialog"
        class="leave-game-dialog"
    >
        <div class="dialog-content">
            <h3>Leave Game?</h3>
            <p>Are you sure you want to leave the game?</p>
            <div class="dialog-buttons">
                <button
                    class="game-button cancel is-muted"
                    @click="leaveDialog?.close()"
                >
                    Cancel
                </button>
                <button
                    class="game-button"
                    @click="leaveGame(true)"
                >
                    Leave
                </button>
            </div>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useBusStore } from '@/client/store/bus.ts'
import { useHistoryStore } from '@/client/store/history.ts'
import { GameType } from '@/shared/types/state.ts'
import UserManual from '@/client/ui/ingame/rightColumn/UserManual.vue'
import { useGameStateStore } from '@/client/store/gameState.ts'
import { usePlayersStore } from '@/client/state/players.ts'
import LogLine from '@/client/ui/ingame/rightColumn/LogLine.vue'
import EngineLog from '@/client/ui/ingame/rightColumn/EngineLog.vue'
import { leaveGame } from '@/client/state/setup.ts'
import { getAuthorColorRgba } from '@/shared/colors.ts'

const gameState = useGameStateStore()
const players = usePlayersStore()
const bus = useBusStore()
const history = useHistoryStore()

/** Tab Management **/

const activeTab = ref('logs')
const isEnlarged = ref(false)
const showArchive = ref(false)

/** Log time toggle **/

const SHOW_TIME_STORAGE_KEY = 'log-show-time'
const showTime = ref(localStorage.getItem(SHOW_TIME_STORAGE_KEY) !== 'false')

function toggleShowTime() {
    showTime.value = !showTime.value
    localStorage.setItem(SHOW_TIME_STORAGE_KEY, String(showTime.value))
}

const tabs = [
    { id: 'logs', title: 'Logs' },
    { id: 'menu', title: 'Menu' },
    { id: 'manual', title: 'Manual' },
]

function toggleEnlarged() {
    isEnlarged.value = !isEnlarged.value
    if (!isEnlarged.value) {
        showArchive.value = false
    }
}

function openArchive() {
    isEnlarged.value = true
    showArchive.value = true
}

/** Logs Tab **/

const logLines = ref<HTMLDivElement>()

const archivedLogEntries = computed(() => {
    if (!showArchive.value || !history.archive) {
        return []
    }
    return history.getArchivedHistory(gameState.gameId).logEntries
})

const displayedLogEntries = computed(() => {
    if (showArchive.value) {
        return [...archivedLogEntries.value, ...history.logEntries]
    }
    return history.logEntries
})

function scrollLog() {
    nextTick(() => {
        const scrollContainer = logLines.value
        if (!scrollContainer) {
            return
        }
        scrollContainer.scrollTop = scrollContainer.scrollHeight
    })
}
function scrollLogIfAtBottom() {
    const scrollContainer = logLines.value
    if (!scrollContainer) {
        return
    }
    const isAtBottom =
        scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 80
    if (isAtBottom) {
        scrollLog()
    }
}

watch(() => history.logEntries.length, scrollLogIfAtBottom)
watch(activeTab, scrollLog)

const chatMessageText = ref('')
const chatInput = ref<HTMLInputElement>()

function sendChatMessage() {
    const text = chatMessageText.value
    if (!text) {
        return
    }

    if (!players.selfPlayer) {
        return
    }
    const authorName = players.selfPlayer.name
    const authorColorRgba = getAuthorColorRgba(players.selfPlayer.rgbaColor)

    const chatMessage = { text, timestamp: new Date(), authorName, authorColorRgba }
    chatMessageText.value = ''
    chatInput.value?.focus()

    history.addChatMessage(chatMessage)
}

/** Menu actions **/

const leaveDialog = ref<HTMLDialogElement>()

/** Manual Heads-up Management **/

const MANUAL_HEADSUP_STORAGE_KEY = 'manual-headsup-dismissed'
// Check if user has already seen the manual heads-up
const dismissed = localStorage.getItem(MANUAL_HEADSUP_STORAGE_KEY)
const hasSeenManualHeadsUp = ref(dismissed === 'true')

const showManualHeadsUp = computed(() => {
    return !hasSeenManualHeadsUp.value && !isEnlarged.value
})

function dismissManualHeadsUp() {
    hasSeenManualHeadsUp.value = true
    localStorage.setItem(MANUAL_HEADSUP_STORAGE_KEY, 'true')
}
</script>

<style lang="scss" scoped>
.tab {
    overflow-y: auto;
    flex-grow: 1;
    padding: 4px;
    background-color: $right-column-section-bg;
    transition: 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    transition-property: padding, transform, width, height, max-width, max-height, box-shadow;
    position: relative;
    z-index: 1;

    &.enlarged {
        padding: 12px;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) translateX(-150px);
        width: 70vw;
        height: 80vh;
        max-width: 1000px;
        max-height: 700px;
        z-index: 1000;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        border-radius: 4px;
        border: 2px solid $purple-grey;
    }

    &.placeholder {
        background-color: $purple-grey;
    }
}

.enlarged-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.1);
    z-index: 999;
    transition: opacity 0.6s ease;
}

.logs {
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .archive-button {
        @include button-grey;
        font-size: 15px;
        padding: 6px 10px;
        margin-bottom: 8px;
    }

    .chat-box {
        display: flex;
        margin-top: 10px;
        margin-bottom: 5px;

        .chat-input {
            flex-grow: 1;
            margin-right: 5px;
        }
    }

    .log-lines-container {
        flex-grow: 1;
        overflow: hidden;
        position: relative;

        .toggle-time-button {
            position: absolute;
            top: 0;
            right: 20px;
            z-index: 10;
            font-size: 11px;
            padding: 2px;
            cursor: pointer;
            border: 2px solid $burgundy-red;
            background: $midnight-purple;
            color: $pearl-grey;
            opacity: 0.6;
            font-family: monospace;

            &:hover {
                opacity: 1;
                color: $ghost-white;
            }
        }

        .log-lines {
            height: 100%;
            overflow-x: auto;
            overflow-y: scroll;
            font-size: 15px;
        }
    }

    &.enlarged {
        font-size: 16px;
    }
}

.menu {
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    .game-button {
        width: 100%;
        max-width: 200px;
    }
}

.manual {
    padding: 12px;
}

.tabs {
    padding-bottom: 4px;
    position: relative;
    z-index: 1001; /* Above the enlarged tab */

    .tab-button {
        margin: 0 6px 0 0;
        padding: 4px 5px;
        border-radius: 0 0 4px 4px;
        border: none;
        color: $pearl-grey;
        background: $purple-grey;
        cursor: pointer;
        transition: all 0.2s ease;

        &:not(.enlarge) {
            font-weight: 600;
            font-style: italic;
        }

        &.active:not(.enlarge) {
            color: $shadow-grey;
            background: $right-column-section-bg;
            cursor: default;
        }

        &:hover:not(.active) {
            filter: brightness(150%);
        }

        &.enlarged {
            border-radius: 4px;
        }

        &.enlarge {
            min-width: 24px;

            &.active {
                color: $vibrant-emerald;
                background-color: rgba($vibrant-emerald, 0.3);
            }
        }
    }
}

@keyframes logAppear {
    0% {
        background-color: transparent;
    }
    10% {
        background-color: $light-teal;
    }
    100% {
        background-color: transparent;
    }
}

.manual-heads-up {
    position: absolute;
    bottom: 10px;
    right: 210px;
    z-index: 1002;
    animation: headsUpAppear 0.5s ease-out;

    &.enlarged {
        display: none;
    }

    .heads-up-content {
        background: $shadow-purple;
        padding: 12px 16px;
        min-width: 260px;
        max-width: 300px;
        color: $ghost-white;
        font-size: 14px;
        line-height: 1.4;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .heads-up-message {
        text-align: left;

        strong {
            color: #ffd700;
        }
    }

    .heads-up-button {
        @include button-grey;
        align-self: flex-end;
        color: $ghost-white;
        padding: 6px 12px;
    }

    .heads-up-arrow {
        position: absolute;
        right: -8px;
        bottom: 0;
        width: 0;
        height: 0;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        border-left: 8px solid $shadow-purple;
    }
}

@keyframes headsUpAppear {
    0% {
        opacity: 0;
        transform: translateX(20px);
    }
    100% {
        opacity: 1;
        transform: translateX(0);
    }
}

.leave-game-dialog {
    border: 2px solid $shadow-grey;
    padding: 0;
    background: $ash-grey;
    max-width: 400px;

    &::backdrop {
        background: rgba(0, 0, 0, 0.5);
    }

    .dialog-content {
        padding: 24px;
        color: $ghost-white;

        h3 {
            margin: 0;
            font-size: 20px;
        }

        p {
            margin: 12px 0 24px 0;
            font-size: 15px;
        }

        .dialog-buttons {
            display: flex;
            gap: 12px;
            justify-content: flex-end;

            .game-button {
                min-width: 80px;
                border-color: $shadow-grey;
            }
        }
    }
}
</style>
